import { Box, Button, Flex, Heading, Icon, Text, useToast } from "@chakra-ui/react";
import { t } from "i18next";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import { FaPlus } from "react-icons/fa";
import {
  ContractListItemDto,
  ProjectDto,
  useListContractsQuery,
  useUpdateContractMutation,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { isFetchBaseQueryError } from "../../../common/errors/isFetchBaseQueryError";
import { SingleSelector } from "../../../common/input/Selector/SingleSelector";
import { ListLoadError } from "../../../common/ListLoadError";
import { CreateContractModal } from "../../contracts/create/CreateContractModal";
import { ContractsTable } from "../../contracts/view-all/ContractsTable";

const limit = 10;

export const Contracts = ({ project }: { project: ProjectDto }) => {
  const authState = useLoggedInWithOrgContextState();

  const [updateContract, { isLoading: isUpdatingContract }] = useUpdateContractMutation();
  const toast = useToast();

  const [skip, setSkip] = useState(0);

  const {
    data: projectContracts,
    isLoading: isLoadingProjectContracts,
    isError: projectContractsFailed,
    refetch: refetchProjectContracts,
  } = useListContractsQuery({
    orgId: authState.selectedOrg.id,
    projects: project.id,
    limit,
    skip,
  });

  const [showModal, setShowModal] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [debouncedSearchName, setDebouncedSearchName] = useState("");
  const [selectedContract, setSelectedContract] = useState<ContractListItemDto>();

  const {
    data: searchedContracts,
    isFetching: isLoadingSearchedContracts,
    isError: contractSearchFailed,
    refetch: retryContractSearch,
  } = useListContractsQuery({
    orgId: authState.selectedOrg.id,
    title: debouncedSearchName ? debouncedSearchName : undefined,
    limit: 10,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSearchName = useCallback(
    debounce((name: string) => setDebouncedSearchName(name), 300),
    []
  );

  const onSelectedContract = async (contract: ContractListItemDto) => {
    setSelectedContract(contract);
    const updatedProjects = Array.from(new Set([...(contract.projects?.map((p) => p.id) ?? []), project.id]));
    try {
      await updateContract({
        contractId: contract.id,
        updateContractRequest: {
          editProjects: updatedProjects,
        },
      }).unwrap();
    } catch (error) {
      if (isFetchBaseQueryError(error) && error.status === 403) {
        toast({
          title: t("The contract could not be connected to the project."),
          description: t("You don't have permission to edit it. Ask an administrator in your organization for edit access."),
          status: "error",
        });
        setSearchName("");
        setSelectedContract(undefined);
      } else {
        toast({ description: t("The contract could not be connected to the project. Try again."), status: "error" });
      }
      return;
    }
    setSearchName("");
    setSelectedContract(undefined);
  };

  return (
    <>
      {showModal && (
        <CreateContractModal project={project} onClose={() => setShowModal(false)} goToContractOnCreation={false} />
      )}
      <Box>
        {authState.selectedOrg.canCreateContract && (
          <Flex columnGap={6} pb={contractSearchFailed ? "2" : "5"}>
            <Box>
              <Button
                leftIcon={<Icon as={FaPlus} />}
                variant="outline"
                colorScheme="teal"
                size="sm"
                onClick={() => setShowModal(true)}
              >
                {t("New contract")}
              </Button>
            </Box>
            <SingleSelector
              size="sm"
              isClearable
              placeholder={{ text: `${t("Connect a contract to this project")}...`, color: "" }}
              isLoading={isLoadingSearchedContracts || isUpdatingContract}
              isDisabled={isUpdatingContract}
              value={selectedContract ? { label: selectedContract.title, value: selectedContract.id } : undefined}
              options={
                contractSearchFailed
                  ? []
                  : (searchedContracts?.contracts
                      .filter((c) => c.canUpdate)
                      .map((c) => ({ value: c.id, label: c.title })) ?? [])
              }
              noOptionsProvidedMessage={contractSearchFailed ? String(t("Could not search contracts.")) : undefined}
              noMatchingOptionsMessage={contractSearchFailed ? String(t("Could not search contracts.")) : undefined}
              inputValue={searchName}
              onInputChange={(name, meta) => {
                const leavingTheSelector = meta.action === "input-blur" || meta.action === "menu-close";
                if (contractSearchFailed && leavingTheSelector) return;
                setSearchName(name);
                handleDebouncedSearchName(name);
              }}
              onChange={async (o) => {
                if (o === null) return;
                const contract = searchedContracts?.contracts.find((c) => c.id === o?.value);
                if (!contract) throw Error("Contract not found");
                await onSelectedContract(contract);
              }}
            />
          </Flex>
        )}
        {authState.selectedOrg.canCreateContract && contractSearchFailed && (
          <Flex align="center" gap="3" pb="5">
            <Text fontSize="sm" color="smError">
              {t("Could not search contracts.")}
            </Text>
            <Button variant="outline" colorScheme="teal" size="sm" onClick={() => void retryContractSearch()}>
              {t("Try again")}
            </Button>
          </Flex>
        )}
        <Heading fontSize="lg" pb="5">
          {t("Contracts connected to this project")}
        </Heading>
        {projectContractsFailed ? (
          <ListLoadError message={t("Could not load contracts.")} onRetry={() => void refetchProjectContracts()} />
        ) : (
          <ContractsTable
            isLoading={isLoadingProjectContracts}
            contracts={projectContracts?.contracts ?? []}
            limit={limit}
            skip={skip}
            setSkip={setSkip}
          />
        )}
      </Box>
    </>
  );
};
